import type { RefObject } from 'react';
import { useRef, useEffect } from 'react';

export default function useEventListener<T extends HTMLElement | Window = HTMLDivElement>(
  eventName: string,
  handler: (event: Event) => void,
  element?: RefObject<T | null>,
) {

  // Create a ref that stores handler
  const savedHandler = useRef<(event: Event) => void>(undefined);

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }

    // Create event listener that calls handler function stored in ref
    const eventListener = (event: Event) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler?.current) {
        savedHandler.current(event);
      }
    };

    targetElement.addEventListener(eventName, eventListener);

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };

  }, [eventName, element, handler]);

}

export { useEventListener };